import { Head, Link } from '@inertiajs/react'
import { ArrowRight, Inbox, MessageSquare } from 'lucide-react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Button } from '@/components/ui/Button'
import type { Breadcrumb } from '@/types/shared'

type ChatroomSummary = {
  id: string
  playerName?: string | null
  teamName?: string | null
  lastMessage?: string | null
  lastMessageAt?: string | null
  lastMessageSender?: string | null
}

export type SchoolChatroomsIndexProps = {
  chatrooms: ChatroomSummary[]
  breadcrumbs: Breadcrumb[]
}

export default function SchoolChatroomsIndex({ chatrooms, breadcrumbs }: SchoolChatroomsIndexProps) {
  return (
    <AppShell>
      <Head title="チャット一覧" />
      <PageHeader
        title="チャット一覧"
        description="選手とのメッセージ履歴を確認し、返信漏れのないコミュニケーションを実現しましょう。"
        breadcrumbs={breadcrumbs}
        actions={
          <Button asChild variant="secondary" size="sm">
            <Link href="/school/players">
              選手を探す
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        }
      />

      <Card padding="lg" className="space-y-4">
        <header className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">進行中のチャット</h2>
            <p className="text-xs text-white/50">最新の更新順で表示しています</p>
          </div>
          <span className="rounded-full bg-white/10 px-4 py-1 text-xs text-white/60">
            {chatrooms.length} 件
          </span>
        </header>

        {chatrooms.length === 0 ? (
          <EmptyState
            icon={<Inbox className="h-8 w-8" />}
            title="チャット履歴がありません"
            description="気になる選手の詳細ページからメッセージを送ると、ここに履歴が表示されます。"
            action={
              <Button asChild size="sm">
                <Link href="/school/players">
                  選手を探す
                  <MessageSquare className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            }
          />
        ) : (
          <div className="space-y-3">
            {chatrooms.map((chatroom) => (
              <Link
                key={chatroom.id}
                href={`/school/chatrooms/${chatroom.id}`}
                className="flex flex-col gap-2 rounded-3xl border border-white/10 bg-white/5 p-4 transition hover:border-white/20 hover:bg-white/10"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-white">{chatroom.playerName ?? '名前未登録'}</p>
                    <p className="text-xs text-white/60">{chatroom.teamName ?? '所属未登録'}</p>
                  </div>
                  {chatroom.lastMessageAt ? (
                    <time className="text-xs text-white/40">{formatDateTime(chatroom.lastMessageAt)}</time>
                  ) : null}
                </div>
                <p className="line-clamp-2 text-xs text-white/70">
                  {chatroom.lastMessage ? chatroom.lastMessage : 'メッセージはまだありません'}
                </p>
                {chatroom.lastMessageSender ? (
                  <p className="text-[11px] text-white/40">最後の送信者: {chatroom.lastMessageSender}</p>
                ) : null}
              </Link>
            ))}
          </div>
        )}
      </Card>
    </AppShell>
  )
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
